import { logger } from '@dealerportal/core';
import { toCicLocale } from '@dealerportal/i18n';
import { useCallback, useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useLocalStorage } from '../../../../react-helpers/src/lib/hooks/useLocalStorage';

const SESSION_ID = 'cicSessionId';

const fetchCatalogVersion = async (catalogId: string) => {
  const response = await globalThis.CiCAPI.content.v2.getCatalog(catalogId);
  const catalog = response.result;
  if (!catalog) {
    logger.error(`Catalog not found with catalogId ${catalogId}`);
    return;
  }
  const catalogVersionId = catalog?.catalogVersions[0]?.id ?? '';
  return catalogVersionId;
};

const CiCScriptId = 'cic-js-script';

type Options = {
  loadScript: boolean;
  /**
   * Site locale (`en` | `pt` | `es`). Mapped to the CiC locale that decides
   * the language of facet and value labels returned by the CiC client.
   */
  locale?: string;
};

type UseCiCAPIResult = {
  loaded: boolean;
  fetchCatalogVersion: (catalogId: string) => Promise<string | undefined>;
};

const configOverrides: IConfig = {
  version: 0,
  contentPlatform: {
    nbWorkers: 4,
    useCatalogCrawler: false,
    useCatalogCrawlerParallelDownloads: false,
    useDesignServices: false,
    skipSearchCatalogDependenciesAttributes: true,
  },
  appInsight: {
    enabled: false,
  },
};

const options: ICiCAPIInitOptions = {
  apiSrcUrl: process.env.NEXT_PUBLIC_CIC_CLIENT_SCRIPT_URL ?? '',
  configOverrides,
  partnership: undefined,
  crawlPartnershipContent: false,
};

export const useCiCAPI = (opts: Options): UseCiCAPIResult => {
  const [loaded, setLoaded] = useState(false);
  const storage = useLocalStorage();

  const init = useCallback(async () => {
    if (!storage) {
      logger.warn('Local storage is not available');
    }
    // Ensure session ID exists before initialization
    let sessionId = storage?.getItem(SESSION_ID);
    if (!sessionId) {
      sessionId = uuid();
      storage?.setItem(SESSION_ID, sessionId);
    }

    const cicLocale = toCicLocale(opts.locale);
    logger.debug(
      'Initializing CiC API with session ID:',
      sessionId,
      'locale:',
      cicLocale
    );
    return await globalThis.CiCAPI.init({
      ...options,
      // `contentPlatform.locale` selects the CiC dictionary used to label
      // facets and facet values; it defaults to en-US when unset.
      configOverrides: {
        ...configOverrides,
        contentPlatform: {
          ...configOverrides.contentPlatform,
          locale: cicLocale,
        } as IConfig['contentPlatform'],
      },
      sessionContext: {
        // Generate a unique ID for each session
        id: sessionId,
        name: 'DealerPortal',
        version: '1.0.0',
      },
    }).then(() => {
      setLoaded(true);
    });
  }, [storage, opts.locale]);

  useEffect(() => {
    logger.debug('useCiCAPI hook mounted, starting initialization');
    if (!opts.loadScript) {
      logger.debug('CiC API script loading is disabled via options');
      return;
    }

    if (process.env.NEXT_PUBLIC_CIC_CLIENT_SCRIPT_URL == null) {
      throw new Error(
        'NEXT_PUBLIC_CIC_CLIENT_SCRIPT_URL environment variable is not set'
      );
    }

    if (document.getElementById(CiCScriptId)) {
      logger.debug('CiC API script is already loaded');
      setLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = options.apiSrcUrl;
    script.async = true;
    script.id = CiCScriptId;

    script.onload = async () => {
      logger.debug('CiC API script loaded');
      try {
        await init();
      } catch (error) {
        // The CiC client script dynamically imports its own chunks at runtime;
        // a failed chunk fetch (stale hash, network blip, remote CDN issue)
        // must not surface as an unhandled promise rejection. Degrade to the
        // catalog UI simply not loading.
        logger.withError(error).error('Failed to initialize CiC API');
      }
    };

    script.onerror = () => {
      logger.error(`Failed to load CiC API script from ${options.apiSrcUrl}`);
    };

    logger.debug('Appending CiC API script to document body');
    document.body.appendChild(script);

    return () => {
      // Clean up the script when component unmounts
      logger.debug('Cleaning up CiC API script');
      document.body.removeChild(script);
    };
  }, []);

  return { loaded, fetchCatalogVersion };
};
